var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import '../icon/icon.js';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { html, literal } from 'lit/static-html.js';
import { SgdfElement } from '../../internal/sgdf-element.js';
import styles from './icon-button.styles.js';
/**
 * @summary Icons buttons are simple, icon-only buttons that can be used for actions and in toolbars.
 * @documentation https://shoelace.style/components/icon-button
 * @status stable
 * @since 2.0
 *
 * @dependency sgdf-icon
 *
 * @event sgdf-blur - Emitted when the icon button loses focus.
 * @event sgdf-focus - Emitted when the icon button gains focus.
 *
 * @csspart base - The component's base wrapper.
 */
let SgdfIconButton = class SgdfIconButton extends SgdfElement {
    constructor() {
        super(...arguments);
        this.hasFocus = false;
        /** The name of a registered custom icon library. */
        this.library = 'solid';
        /**
         * A description that gets read by assistive devices. For optimal accessibility, you should always include a label
         * that describes what the icon button does.
         */
        this.label = '';
        /** Disables the button. */
        this.disabled = false;
        this.border = false;
        this.submit = false;
    }
    handleBlur() {
        this.hasFocus = false;
        this.emit('sgdf-blur');
    }
    handleFocus() {
        this.hasFocus = true;
        this.emit('sgdf-focus');
    }
    handleClick(event) {
        if (this.disabled) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
    /** Simulates a click on the icon button. */
    click() {
        this.button.click();
    }
    /** Sets focus on the icon button. */
    focus(options) {
        this.button.focus(options);
    }
    /** Removes focus from the icon button. */
    blur() {
        this.button.blur();
    }
    render() {
        const isLink = this.href ? true : false;
        const tag = isLink ? literal `a` : literal `button`;
        return html `
      <${tag}
        part="base"
        class=${classMap({
            'icon-button': true,
            'icon-button--disabled': !isLink && this.disabled,
            'icon-button--focused': this.hasFocus,
            'icon-button--border': this.border,
        })}
        ?disabled=${ifDefined(isLink ? undefined : this.disabled)}
        type=${ifDefined(isLink ? undefined : this.submit ? 'submit' : 'button')}
        href=${ifDefined(isLink ? this.href : undefined)}
        target=${ifDefined(isLink ? this.target : undefined)}
        download=${ifDefined(isLink ? this.download : undefined)}
        rel=${ifDefined(isLink && this.target ? 'noreferrer noopener' : undefined)}
        role=${ifDefined(isLink ? undefined : 'button')}
        aria-disabled=${this.disabled ? 'true' : 'false'}
        aria-label="${this.label}"
        tabindex=${this.disabled ? '-1' : '0'}
        @blur=${this.handleBlur}
        @focus=${this.handleFocus}
        @click=${this.handleClick}
      >
        <sgdf-icon
          class="icon-button__icon"
          name=${ifDefined(this.name)}
          library=${ifDefined(this.library)}
          src=${ifDefined(this.src)}
          aria-hidden="true"
        ></sgdf-icon>
      </${tag}>
    `;
    }
};
SgdfIconButton.styles = styles;
__decorate([
    query('.icon-button')
], SgdfIconButton.prototype, "button", void 0);
__decorate([
    state()
], SgdfIconButton.prototype, "hasFocus", void 0);
__decorate([
    property()
], SgdfIconButton.prototype, "name", void 0);
__decorate([
    property()
], SgdfIconButton.prototype, "library", void 0);
__decorate([
    property()
], SgdfIconButton.prototype, "src", void 0);
__decorate([
    property()
], SgdfIconButton.prototype, "href", void 0);
__decorate([
    property()
], SgdfIconButton.prototype, "target", void 0);
__decorate([
    property()
], SgdfIconButton.prototype, "download", void 0);
__decorate([
    property()
], SgdfIconButton.prototype, "label", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SgdfIconButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SgdfIconButton.prototype, "border", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SgdfIconButton.prototype, "submit", void 0);
SgdfIconButton = __decorate([
    customElement('sgdf-icon-button')
], SgdfIconButton);
export default SgdfIconButton;
//# sourceMappingURL=icon-button.js.map